import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Copy, Check, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { FROZEN_MODE } from "@/config/frozen";

const InlineCouponStrip = () => {
  const [coupon, setCoupon] = useState<{ code: string; discount_percent: number; applicable_on: string } | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (FROZEN_MODE) return; // No backend calls while frozen.
    const fetchPublicCoupon = async () => {
      const { data } = await supabase
        .from("coupons")
        .select("code, discount_percent, applicable_on")
        .eq("status", "active")
        .eq("is_public", true)
        .maybeSingle();
      if (data) setCoupon(data);
    };
    fetchPublicCoupon();
  }, []);

  if (!coupon) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(coupon.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const appliesTo = coupon.applicable_on === "both" ? "all plans" : coupon.applicable_on === "demo" ? "Demo plans" : "Renewal plans";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/20 bg-primary/5 backdrop-blur-sm text-sm"
    >
      <Sparkles className="h-3.5 w-3.5 text-accent shrink-0" />
      <span className="text-muted-foreground">
        Use{" "}
        <button
          onClick={handleCopy}
          className="inline-flex items-center gap-1 font-mono font-bold text-primary hover:text-primary/80 transition-colors"
        >
          {coupon.code}
          {copied ? <Check className="h-3 w-3 text-accent" /> : <Copy className="h-3 w-3" />}
        </button>
        {" "}for <span className="font-semibold text-foreground">{coupon.discount_percent}% off</span> {appliesTo}
      </span>
    </motion.div>
  );
};

export default InlineCouponStrip;
